import * as React from 'react';
import {
  FormEvent,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { ThemeProvider } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import { FormikProps, useFormik } from 'formik';
import keys from 'lodash/keys';

import { compareFileUploads } from '../../../../shared/utils/compare-file-uploads/compare-file-uploads.util';
import { convertToSentenceCase } from '../../../../shared/utils/convert-to-sentence-case/convert-to-sentence-case.util';
import { useAppDispatch } from '../../../../store/hooks';
import { ChangeDetectorContext } from '../../../shared/components/change-detector-context/ChangeDetectorContext';
import DragAndDrop from '../../../shared/components/drag-and-drop/DragAndDrop';
import Dropdown from '../../../shared/components/dropdown/Dropdown';
import Box from '../../../shared/components/ui/Box/Box';
import Button from '../../../shared/components/ui/Button/Button';
import CharacterCounter from '../../../shared/components/ui/CharacterCounter/CharacterCounter';
import FormControl from '../../../shared/components/ui/FormControl/FormControl';
import Input from '../../../shared/components/ui/Input/Input';
import { FileMetadataTypesEnum } from '../../../shared/enums/file-metadata-types.enum';
import { FileSizes } from '../../../shared/enums/file-sizes.enum';
import useChangePrompt from '../../../shared/hooks/use-change-prompt/use-change-prompt.hook';
import { formTheme } from '../../../shared/themes/form-theme/form.theme';
import { castTo } from '../../../shared/utils/cast-to';
import { convertBytes } from '../../../shared/utils/convert-bytes/convert-bytes.util';
import { getRenderInput } from '../../../shared/utils/get-render-input/get-render-input.util';
import {
  mapPlatformModelToItemLabelModel,
  mapStatesEnumToItemLabelModel,
} from '../../../shared/utils/mapping/mapping.utils';
import { optionEquality } from '../../../shared/utils/option-equality/option-equality.util';
import { useSetApkDragAndDropTouched } from '../../hooks/use-set-apk-drag-and-drop-touched/use-set-apk-drag-and-drop-touched.hook';
import { useSetApkFiles } from '../../hooks/use-set-apk-files/use-set-apk-files.hook';
import { appsFilesActions } from '../../store/slices/apps-files.slice';
import { useGetDeleteHandler } from './hooks/use-get-delete-handler/use-get-delete-handler.hook';
import { useGetReleasesFormSelectors } from './hooks/use-get-releases-form-selectors/use-get-releases-form-selectors.hook';
import { useOnSubmitReleasesForm } from './hooks/use-on-submit-releases-form/use-on-submit-releases-form.hook';
import { ReleasesFormProps } from './models/releases-form-props.model';
import { ReleasesFormikModel } from './models/releases-formik.model';
import { getReleasesFormSchema } from './schemas/releases-form.schema';
import { getReleasesFormValues } from './utils/get-releases-form.values';

const ReleasesForm: FunctionComponent<ReleasesFormProps> = ({
  data,
  submitThunk,
  isFormOpen,
  changeFormState,
}: PropsWithChildren<ReleasesFormProps>) => {
  const cdContext = useContext(ChangeDetectorContext);
  const { id: appId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const parsedAppId = parseInt(appId as string, 10);

  const cancelAction = useChangePrompt(
    true,
    () => {
      navigate('../');
    },
    <span>Warning</span>,
    <span>Filled information will be lost. Leave anyway?</span>
  );

  const { appsFiles, hasFormError } = useGetReleasesFormSelectors();

  useSetApkFiles(data);

  const deleteActionHandler = useGetDeleteHandler(parsedAppId, data);

  const formikInitialValues = useMemo<Partial<ReleasesFormikModel>>(
    () => getReleasesFormValues(appsFiles, data),
    [appsFiles]
  );

  const onSubmitForm = useOnSubmitReleasesForm(
    parsedAppId,
    submitThunk,
    changeFormState,
    data
  );

  const formik: FormikProps<Partial<ReleasesFormikModel>> = useFormik<
    Partial<ReleasesFormikModel>
  >({
    initialValues: formikInitialValues,
    validationSchema: getReleasesFormSchema(),
    onSubmit: onSubmitForm,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: false,
  });

  const platformsRenderInput = getRenderInput<Partial<ReleasesFormikModel>>(
    'Platforms',
    'platforms',
    formik.errors,
    formik.touched
  );

  const statusRenderInput = getRenderInput<Partial<ReleasesFormikModel>>(
    'Status',
    'status',
    formik.errors,
    formik.touched,
    true
  );

  const isFileArrayModified = compareFileUploads(
    data?.apkFiles.map(x => x.id),
    formik.values.defaultApkFiles?.map(x => x.id)
  );

  const showApkError =
    !!formik.errors.defaultApkFiles &&
    (formik.touched.defaultApkFiles || formik.submitCount > 0);

  useEffect(() => {
    cdContext?.setIsChanged(
      (formik.dirty && keys(formik.touched).length > 0) || isFileArrayModified
    );
  }, [formik.dirty, formik.touched, isFileArrayModified]);

  useEffect(() => {
    if (hasFormError) {
      dispatch(appsFilesActions.resetAppsFilesState());
    }
  }, [hasFormError]);

  useEffect(() => {
    const apkFieldsValues = getReleasesFormValues(appsFiles, data);
    formik.setValues(prev => ({
      ...prev,
      ...apkFieldsValues,
      description: prev.description,
      status: prev.status,
    }));
  }, [appsFiles]);

  useSetApkDragAndDropTouched<Partial<ReleasesFormikModel>>(
    formik.values.defaultApkFiles,
    formik.setFieldTouched,
    'defaultApkFiles'
  );

  return (
    <>
      <ThemeProvider theme={formTheme}>
        <Box
          sx={{
            my: '48px',
            display: 'flex',
            justifyContent: 'center',
          }}
          data-testid="create-app-releases"
        >
          <Box
            component={'form'}
            onSubmit={event =>
              formik.handleSubmit(castTo<FormEvent<HTMLFormElement>>(event))
            }
            sx={{
              maxWidth: '500px',
              margin: 'auto',
              width: '100%',
              px: '6px',
              boxSizing: 'border-box',
            }}
          >
            <FormControl sx={{ flexWrap: 'wrap' }}>
              <DragAndDrop
                name="ReleasesFormDnD"
                removeHandler={appsFilesActions.deleteApk}
                uploadHandler={appsFilesActions.setApk}
                multiple={true}
                label="APK Bundles & Files"
                isRequired={true}
                hasError={showApkError}
                files={formik.values.defaultApkFiles}
                helperText="Drag or drop APK file here to upload"
                buttonLabel={'Browse files'}
                fileTypeAccepted={'.apk'}
                previewType={FileMetadataTypesEnum.APK}
                fileType={FileMetadataTypesEnum.APK}
                splitIntoChunks={true}
                featureId={parsedAppId}
              ></DragAndDrop>

              {showApkError && (
                <FormHelperText error>
                  {formik.errors.defaultApkFiles}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl sx={{ flexWrap: 'wrap' }}>
              <Input
                id="version"
                label="Version name"
                variant="filled"
                disabled={true}
                name="version"
                type="text"
                fullWidth={true}
                value={formik.values.version}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={!!formik.errors.version}
                helperText={!!formik.errors.version && formik.errors.version}
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                    disabled: 'input-disabled',
                  },
                }}
                InputLabelProps={{
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
            </FormControl>

            <FormControl sx={{ flexWrap: 'wrap' }}>
              <Input
                id="versionCode"
                label="Version code"
                variant="filled"
                disabled={true}
                name="versionCode"
                type="text"
                fullWidth={true}
                value={formik.values.versionCode ?? ''}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={!!formik.errors.versionCode}
                helperText={
                  !!formik.errors.versionCode && formik.errors.versionCode
                }
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                    disabled: 'input-disabled',
                  },
                }}
                InputLabelProps={{
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <Dropdown
                endpoint="/platforms"
                dataMap={mapPlatformModelToItemLabelModel}
                autoComplete={{
                  className: 'dropdown-autocomplete',
                  sx: {
                    width: '100%',
                  },
                  classes: {
                    popupIndicator: 'mui-icon-button',
                    tag: 'mui-chip-root',
                    inputRoot: 'input-disabled',
                  },
                  value: formik.values.platforms,
                  disabled: true,
                  onChange: (event, value) => {
                    formik.setFieldValue('platforms', value);
                  },
                  isOptionEqualToValue: optionEquality,
                  multiple: true,
                  filterSelectedOptions: true,
                  id: 'platforms',
                  renderInput: platformsRenderInput,
                }}
              />
            </FormControl>

            <FormControl sx={{ flexWrap: 'wrap' }}>
              <Input
                id="sizeInBytes"
                label="Size"
                variant="filled"
                disabled={true}
                name="sizeInBytes"
                type="text"
                fullWidth={true}
                value={
                  formik.values.sizeInBytes !== undefined &&
                  formik.values.sizeInBytes !== 0
                    ? convertBytes(
                        formik.values.sizeInBytes,
                        FileSizes.MegaBytes,
                        2,
                        'MB'
                      )
                    : ''
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={!!formik.errors.sizeInBytes}
                helperText={
                  !!formik.errors.sizeInBytes && formik.errors.sizeInBytes
                }
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                    disabled: 'input-disabled',
                  },
                }}
                InputLabelProps={{
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <Input
                sx={{
                  width: '488px',
                }}
                label="Release description"
                variant="filled"
                id="description"
                name="description"
                type="text"
                multiline={true}
                fullWidth={true}
                rows={9}
                value={formik.values.description}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={
                  formik.touched.description && !!formik.errors.description
                }
                helperText={
                  !!formik.errors.description &&
                  formik.touched.description &&
                  formik.errors.description
                }
                InputProps={{
                  disableUnderline: true,
                  classes: {
                    focused: 'input-focused',
                  },
                }}
                inputProps={{
                  maxLength: '4000',
                }}
                InputLabelProps={{
                  required: true,
                  classes: {
                    filled: 'input-label-filled',
                    error: 'input-label-error',
                    focused: 'input-label-focused',
                  },
                }}
              />

              <CharacterCounter
                className={'input-helper-text'}
                current={formik.values?.description?.length ?? 0}
                max={4000}
              />
            </FormControl>

            <FormControl>
              <Dropdown
                endpoint="/release-statuses"
                dataMap={mapStatesEnumToItemLabelModel}
                autoComplete={{
                  className: 'dropdown-autocomplete',
                  sx: {
                    width: '100%',
                  },
                  value: formik.values.status,
                  isOptionEqualToValue: optionEquality,
                  onChange: (event, value) => {
                    formik.setFieldValue('status', value);
                  },
                  onBlur: formik.handleBlur,
                  filterSelectedOptions: true,
                  id: 'status',
                  getOptionLabel: o => convertToSentenceCase(o.label),
                  renderInput: statusRenderInput,
                }}
              />
            </FormControl>

            <FormControl
              classes={{
                root: 'button-container',
              }}
            >
              <Box
                sx={{
                  flexWrap: 'wrap',
                  display: 'flex',
                  gap: '24px',
                }}
              >
                {data && (
                  <Button
                    type="button"
                    variant="contained"
                    classes={{
                      root: 'button',
                    }}
                    className="btn btn-white"
                    onClick={deleteActionHandler}
                  >
                    Delete
                  </Button>
                )}

                {isFormOpen && (
                  <Button
                    type="button"
                    variant="contained"
                    classes={{
                      root: 'button',
                    }}
                    className="btn btn-white"
                    onClick={cancelAction}
                  >
                    Cancel
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  classes={{
                    root: 'button',
                  }}
                  className="btn btn-primary"
                >
                  Save
                </Button>
              </Box>
            </FormControl>
          </Box>
        </Box>
      </ThemeProvider>
    </>
  );
};
export default ReleasesForm;
